/**
 * Checks if a product is under an active launch campaign and whether
 * the current user is allowed to purchase.
 *
 * Returns:
 *   { blocked: false }                            — no restriction
 *   { blocked: true, redirectLogin: true, slug }  — must log in first
 *   { blocked: true, reason, slug }               — logged in but restricted
 *   { blocked: true, maxQtyExceeded: true, max, slug } — over per-user limit
 */

const isBrowser = typeof window !== 'undefined';

function getApiBase() {
  const base = (typeof process !== 'undefined' && process.env?.BASE_FRONT)
    ? process.env.BASE_FRONT
    : 'https://dashboard.dondelanegra.cl';
  return base.replace(/\/$/, '');
}

function getAuthToken() {
  if (!isBrowser) return null;
  return localStorage.getItem('auth_token');
}

function getCartQtyForSku(sku) {
  if (!isBrowser) return 0;
  try {
    const cart = JSON.parse(localStorage.getItem('products_cart') || '[]');
    return cart
      .filter(item => item.sku === sku)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);
  } catch {
    return 0;
  }
}

/**
 * Valida que ninguno de los ítems del carrito supere el max_qty_per_user
 * de su Launch Campaign. Pensado para gatillarse antes del checkout/pago.
 *
 * Returns:
 *   { valid: true }
 *   { valid: false, errors: [{ productId, productName, qty, max, slug }] }
 */
export async function validateCartLaunchCampaigns(cart) {
  if (!isBrowser) return { valid: true };
  if (!Array.isArray(cart) || cart.length === 0) return { valid: true };

  const base = getApiBase();
  const errors = [];

  for (const item of cart) {
    if (!item?.product_id) continue;
    try {
      const res = await fetch(
        `${base}/api/launch-campaigns/by-product/${item.product_id}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!res.ok) continue;
      const data = await res.json();
      const campaign = data?.campaign;
      if (!campaign || !campaign.max_qty_per_user) continue;
      const max = Number(campaign.max_qty_per_user);
      const qty = Number(item.quantity || 0);
      if (qty > max) {
        errors.push({
          productId: item.product_id,
          productName: item.name || 'Producto',
          qty,
          max,
          slug: campaign.slug,
        });
      }
    } catch {
      // En caso de error de red no bloqueamos: el backend re-valida al crear la orden.
    }
  }

  return errors.length === 0 ? { valid: true } : { valid: false, errors };
}

export async function checkLaunchCampaignAccess(internalId, sku, requestedQty = 1) {
  if (!isBrowser) return { blocked: false };

  try {
    const base = getApiBase();

    const campaignRes = await fetch(`${base}/api/launch-campaigns/by-product/${internalId}`, {
      headers: { Accept: 'application/json' },
    });
    if (!campaignRes.ok) return { blocked: false };

    const { campaign } = await campaignRes.json();
    if (!campaign) return { blocked: false };

    const slug = campaign.slug;
    const token = getAuthToken();

    // max_qty_per_user client-side check (cart quantity)
    if (campaign.max_qty_per_user) {
      const alreadyInCart = getCartQtyForSku(sku);
      if (alreadyInCart + requestedQty > campaign.max_qty_per_user) {
        return {
          blocked: true,
          maxQtyExceeded: true,
          max: campaign.max_qty_per_user,
          slug,
        };
      }
    }

    const statusRes = await fetch(`${base}/api/launch-campaigns/${slug}/access-status`, {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!statusRes.ok) return { blocked: false };

    const status = await statusRes.json();

    if (!status.is_authenticated && campaign.requires_login) {
      return { blocked: true, redirectLogin: true, slug };
    }

    if (!status.can_purchase) {
      return { blocked: true, reason: status.reason || 'No disponible en este momento.', slug };
    }

    return { blocked: false };
  } catch {
    // On network error, don't block — backend will validate on order creation
    return { blocked: false };
  }
}
